

export default class ColorScaleCompService {

    public static HLtoLUV(hue: number, luminance: number) {

        const maxS = 93.727325;
        const kL = 7.67;

        // With the luminance (L) we can calculate the saturation (S) as we've seen above.
        let S = 0;
        const L = kL * Math.sqrt(luminance);
        // We add 19.06 to the hue so hue=0 is red as in the HSB mode (optional).

        const H = +hue + 19.06;
        if (L >= 73 || L == 0) {
            S = 0;
        } else if (73 > L && L > 57) {
            S = 93.727325 - maxS * (L - 57) / 16;
        } else if (57 >= L) {
            S = L * maxS / 57;
        }

        // U and V values are calculated drawing a circle which radius is S<
        // so we'll get the color whose distance to the center is S and
        // its position in the circle is H (hue).
        const U = S * Math.cos(H * Math.PI / 180);
        const V = S * Math.sin(H * Math.PI / 180);

        return [L, U, V];
    }

    public static HLtoXYZ(hue: number, luminance: number) {
        const colorLUV = this.HLtoLUV(hue, luminance);

        // Constants
        const k = 0.0011070564598794539; //(3/29)^3


        // We adjust luminance from a 0-200 to a 0-100 range.
        const L = colorLUV[0] * 2;
        const U = colorLUV[1];
        const V = colorLUV[2];

        // Now we transform the LUV color into XYZ.
        let X, Y, Z;

        // And we do the calculations to transform the color from CIELUV to CIEXYZ
        // as the CIE has published.
        if (L != 0) {
            const un = 0.2009;
            const vn = 0.4610;
            const Xn = 0.312713;
            const Yn = 0.329016;
            const u = U / (13.0 * L) + un;
            const v = V / (13.0 * L) + vn;
            if (L <= 8) {
                Y = Yn * L * k;
            } else {
                Y = Yn * Math.pow((L + 16) / 116.0, 3);
            }
            X = (Y * 9 * u) / (4.0 * v);
            Z = Y * (12 - 3 * u - 20 * v) / (4 * v);
        } else {
            X = 0;
            Y = 0;
            Z = 0;
        }

        return [X, Y, Z];
    }

    public static HLtoRGB(hue: number, luminance: number) {
        const colorXYZ = this.HLtoXYZ(hue, luminance);
        const X = colorXYZ[0];
        const Y = colorXYZ[1];
        const Z = colorXYZ[2];

        // Now we transform the XYZ color to RGB using the CIE documentation.
        let R = + 3.2406 * X - 1.5372 * Y - 0.4986 * Z;
        let G = - 0.9689 * X + 1.8758 * Y + 0.0415 * Z;
        let B = + 0.0555 * X - 0.2040 * Y + 1.0570 * Z;

        // We transform the RGB range from [0-1], to [0-255] trying not to get 
        // values greater than 255. 
        // (The constants are not exact so we could get impossible colors).
        R = R * 255 > 255 ? 255 : R * 255;
        G = G * 255 > 255 ? 255 : G * 255;
        B = B * 255 > 255 ? 255 : B * 255;

        // We'll also delete negative values.
        R = R < 0 ? 0 : R;
        G = G < 0 ? 0 : G;
        B = B < 0 ? 0 : B;

        return [R, G, B];
    }

    public static HLtoHEX(hue: number, luminance: number) {
        const colorRGB = this.HLtoRGB(hue, luminance);

        const R = colorRGB[0];
        const G = colorRGB[1];
        const B = colorRGB[2];

        // If you want to work with RGB mode, stop here.
        // But sometimes is easier to work with HEX mode so now we'll
        // transform our color from RGB to HEX.
        let hexR = Math.round(R).toString(16);
        let hexG = Math.round(G).toString(16);
        let hexB = Math.round(B).toString(16);
        hexR = hexR.length == 1 ? "0" + hexR : hexR;
        hexG = hexG.length == 1 ? "0" + hexG : hexG;
        hexB = hexB.length == 1 ? "0" + hexB : hexB;

        return "#" + hexR + hexG + hexB;
    }

    // ------------------------------------------------------

    public static RGBtoXYZ(R: number, G: number, B: number) {
        const X = (0.4124564 * R + 0.3575761 * G + 0.1804375 * B) / 255;
        const Y = (0.2126729 * R + 0.7151522 * G + 0.0721750 * B) / 255;
        const Z = (0.0193339 * R + 0.1191920 * G + 0.9503041 * B) / 255;
        return [X, Y, Z];
    }

    public static XYZtoLUV(X: number, Y: number, Z: number) {
        const k = 0.0011070564598794539; //(3/29)^3
        const Yn = 0.329016;
        const un = 0.2009;
        const vn = 0.4610;

        let L = 0;
        let u = 0;
        let v = 0;

        if (!(X == 0 && Y == 0 && Z == 0)) {
            if (Y / (Yn * k) <= 8) {
                L = Y / (Yn * k);
            } else {
                L = 116 * Math.pow(Y / Yn, 1 / 3) - 16;
            }
            u = (4 * X) / (X + 15 * Y + 3 * Z);
            v = (9 * Y) / (X + 15 * Y + 3 * Z);
        }

        const U = 13 * L * (u - un);
        const V = 13 * L * (v - vn);

        return [L / 2, U, V];
    }

    public static LUVtoHL(L: number, U: number, V: number): number[] {
        // constants
        const kL = 7.67;
        const maxS = 93.727325;

        let H = -19.06 + Math.atan2(V, U) * 180 / Math.PI;

        H = H < 0 ? H + 360 : H;

        let S = 0;
        if (L >= 73 || L == 0) {
            S = 0;
        } else if (73 > L && L > 57) {
            S = 93.727325 - maxS * (L - 57) / 16;
        } else if (57 >= L) {
            S = L * maxS / 57;
        }

        S = U / Math.cos(H * Math.PI / 180);

        const luminance = Math.pow(L / kL, 2);

        return [H, luminance, S];
    }

    public static RGBtoLUV(R: number, G: number, B: number) {
        const colorXYZ = this.RGBtoXYZ(R, G, B);
        const colorLUV = this.XYZtoLUV(colorXYZ[0], colorXYZ[1], colorXYZ[2]);

        return colorLUV;
    }

    public static RGBtoHL(R: number, G: number, B: number): number[] {
        const colorXYZ = this.RGBtoXYZ(R, G, B);
        const colorLUV = this.XYZtoLUV(colorXYZ[0], colorXYZ[1], colorXYZ[2]);
        const colorHL = this.LUVtoHL(colorLUV[0], colorLUV[1], colorLUV[2]);
        return colorHL;
    }

    public static RGBtoNote(R: number, G: number, B: number) {
        const colorHL = this.RGBtoHL(R, G, B);
        const H = colorHL[0];
        const L = colorHL[1];

        const notes = ['C', 'G', 'D', 'A', 'E', 'B', 'F#', 'C#', 'G#', 'D#', 'A#', 'F'];

        const octave = Math.floor(L / 10);

        return `${notes[(Math.floor((H + 15) / 30)) % 12]}${octave}`;
    }
}
